import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../utils/currency';
import { fetchPayrollRun, fetchPayrollLineItems, downloadPayslips, fetchStaffProfiles } from '../api';

const PayslipView = ({ authAxios, addToast, runId, onBack }) => {
  const [run, setRun] = useState(null);
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [runData, itemsData, staffData] = await Promise.all([
          fetchPayrollRun(authAxios, runId),
          fetchPayrollLineItems(authAxios, { payroll_run: runId }),
          fetchStaffProfiles(authAxios),
        ]);
        setRun(runData);
        setItems(itemsData.results || itemsData);
        const staffMap = {};
        (staffData.results || staffData).forEach(s => { staffMap[s.id] = s; });
        setStaff(staffMap);
      } catch {
        addToast('Failed to load payslip data', 'error');
      }
      setLoading(false);
    };
    load();
  }, [authAxios, runId, addToast]);

  const handleDownload = async () => {
    try {
      const resp = await downloadPayslips(authAxios, runId);
      const blob = resp.data || resp;
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `payslips_run_${runId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      addToast('Failed to download payslips', 'error');
    }
  };

  if (loading) return <div className="p-8 text-center">Loading payslip data...</div>;
  if (!run) return <div className="p-8 text-center text-red-500">Payroll run not found</div>;

  // Group items by staff_profile
  const grouped = {};
  items.forEach(item => {
    const spId = item.staff_profile;
    if (!grouped[spId]) grouped[spId] = { earnings: [], deductions: [], gross: 0, totalDed: 0 };
    const g = grouped[spId];
    const amt = parseFloat(item.amount || 0);
    if (['SALARY', 'BONUS', 'OTHER_ALLOWANCE'].includes(item.line_type)) {
      g.earnings.push(item);
      g.gross += amt;
    } else {
      g.deductions.push(item);
      g.totalDed += amt;
    }
  });

  const staffIds = Object.keys(grouped);

  const fmt = (v) => formatCurrency(v || 0);

  const renderStaffPayslip = (spId) => {
    const g = grouped[spId];
    const profile = staff[spId];
    const name = profile
      ? `${profile.user_first_name || ''} ${profile.user_last_name || ''}`.trim() || profile.username
      : `Staff #${spId}`;
    const net = g.gross - g.totalDed;

    return (
      <div key={spId} className="border rounded-lg p-4 mb-4">
        <h4 className="font-semibold text-lg mb-3">{name}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h5 className="text-sm font-medium text-gray-600 mb-2">Earnings</h5>
            {g.earnings.map(e => (
              <div key={e.id} className="flex justify-between text-sm py-1 border-b border-gray-100">
                <span>{e.description || e.line_type_display || e.line_type}</span>
                <span>{fmt(e.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between font-semibold mt-1 pt-1 border-t">
              <span>Gross Pay</span><span>{fmt(g.gross)}</span>
            </div>
          </div>
          <div>
            <h5 className="text-sm font-medium text-gray-600 mb-2">Deductions</h5>
            {g.deductions.map(d => (
              <div key={d.id} className="flex justify-between text-sm py-1 border-b border-gray-100">
                <span>{d.description || d.line_type_display || d.line_type}</span>
                <span>{fmt(d.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between font-semibold mt-1 pt-1 border-t">
              <span>Total Deductions</span><span>{fmt(g.totalDed)}</span>
            </div>
          </div>
        </div>
        <div className="mt-3 pt-2 border-t-2 flex justify-between text-lg font-bold">
          <span>Net Pay</span>
          <span className="text-green-700">{fmt(net)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Payslips</h2>
          <p className="text-sm text-gray-600">
            Run #{run.id} · {run.pay_period_start} — {run.pay_period_end}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleDownload} className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700">
            Download All (PDF)
          </button>
          {onBack && (
            <button onClick={onBack} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Back</button>
          )}
        </div>
      </div>

      {/* Staff filter */}
      {staffIds.length > 1 && (
        <div className="mb-4">
          <label className="text-sm text-gray-600 mr-2">Filter by employee:</label>
          <select
            className="border rounded p-1.5 text-sm"
            value={selectedStaff || ''}
            onChange={e => setSelectedStaff(e.target.value || null)}
          >
            <option value="">All Employees ({staffIds.length})</option>
            {staffIds.map(spId => {
              const p = staff[spId];
              const n = p ? `${p.user_first_name || ''} ${p.user_last_name || ''}`.trim() || p.username : `Staff #${spId}`;
              return <option key={spId} value={spId}>{n}</option>;
            })}
          </select>
        </div>
      )}

      {(selectedStaff ? [selectedStaff] : staffIds).map(spId => renderStaffPayslip(spId))}
      {staffIds.length === 0 && <p className="text-center text-gray-500 py-8">No payslip data for this run</p>}
    </div>
  );
};

export default PayslipView;
